<script setup lang="ts">
import { useDefaults, useTheme } from '#imports'
import type { Themeable } from '#theme'

export interface UILoaderProps extends Themeable {
    bg?: boolean
    inline?: boolean
    width?: string
    height?: string
    text?: string | false
}

const _props = withDefaults(defineProps<UILoaderProps>(), {
    color: undefined
})
const props = useDefaults({
    props: _props,
    defaults: {
        color: 'secondary'
    }
})

const { themeClass } = useTheme(() => {
    return {
        color: props.color
    }
})
</script>

<template>
    <div
        class="loader"
        :class="{ 'loader--inline': props.inline, 'loader--bg': props.bg, ...themeClass }"
    >
        <div
            :style="{
                height: props.height,
                width: props.width
            }"
        >
            <span />
            <span />
            <span />

            <p v-if="props.text || $slots.default">
                <slot>{{ props.text }}</slot>
            </p>
        </div>
    </div>
</template>

<style lang="scss" scoped>
$scaleUpTime: 2s;
$staggerDelay: 0.3s;
$easeInOutBackCustom: cubic-bezier(0.57, 0.22, 0.37, 1.68);

.loader {
    z-index: z(loader);

    color: var(--color, $brand-primary); // color used to enable use of currentColor in child elements

    div {
        display: flex;
        position: relative;
        justify-content: space-between;
        align-items: center;
        width: 48px;
        height: 48px;
        transition: opacity 0.3s $easeOutQuart;
    }

    &:not(.loader--inline) div,
    p {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    p {
        top: 100%;
        font-size: $font-size-sm;
        white-space: nowrap;
    }

    span {
        position: relative;
        width: 30%;
        height: 30%;
        transform: scale(0);
        border-radius: var(--border-radius-sm, $border-radius-sm);
        opacity: 0;
        background: currentcolor;
        transform-origin: 50% 50%;
        animation: slotRoll $scaleUpTime $easeInOutBackCustom infinite;

        &:nth-child(2) {
            animation-delay: $staggerDelay;
        }

        &:nth-child(3) {
            animation-delay: $staggerDelay * 2;
        }
    }

    &--bg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: inherit;
    }

    &--inline {
        display: inline-flex;
        margin-left: 15px;
    }
}

@keyframes slotRoll {
    0% {
        transform: translate3d(0, -100%, 0) scale(0.5);
        opacity: 0;
    }

    15%,
    65% {
        transform: translate3d(0, 0, 0) scale(1);
        opacity: 1;
    }

    80%,
    100% {
        transform: translate3d(0, 100%, 0) scale(0.5);
        opacity: 0;
    }
}
</style>
